import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { heroImages } from '../data/translations';

const HeroSection = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    setTimeout(() => setIsTransitioning(false), 800);
  }, [isTransitioning]);

  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
    setTimeout(() => setIsTransitioning(false), 800);
  }, [isTransitioning]);

  // Auto-advance slideshow
  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToEvent = () => {
    document.getElementById('evenement')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero-section min-h-screen bg-[#1a1c1b] relative overflow-hidden flex items-center">
      {/* Slideshow Background */}
      <div className="absolute inset-0">
        {heroImages.map((image, index) => (
          <div
            key={image.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image.url}
              alt={image.alt}
              className="w-full h-full object-cover"
              loading={index === 0 ? 'eager' : 'lazy'}
            />
            {/* Dark overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a1c1b] via-[#1a1c1b]/80 to-[#1a1c1b]/40" />
          </div>
        ))}
      </div>

      {/* Slideshow Controls */}
      <div className="absolute bottom-8 right-8 z-20 flex items-center gap-4">
        <button
          onClick={prevSlide}
          className="w-10 h-10 rounded-full border border-[#d9fb06]/50 flex items-center justify-center text-[#d9fb06] hover:bg-[#d9fb06]/20 transition-all"
          disabled={isTransitioning}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        {/* Slide indicators */}
        <div className="flex gap-2">
          {heroImages.map((image, index) => (
            <button
              key={image.id}
              onClick={() => !isTransitioning && setCurrentSlide(index)}
              className={`transition-all ${
                index === currentSlide
                  ? 'w-8 h-2 bg-[#d9fb06] rounded-full'
                  : 'w-2 h-2 bg-[#d9fb06]/40 rounded-full hover:bg-[#d9fb06]/60'
              }`}
            />
          ))}
        </div>
        
        <button
          onClick={nextSlide}
          className="w-10 h-10 rounded-full border border-[#d9fb06]/50 flex items-center justify-center text-[#d9fb06] hover:bg-[#d9fb06]/20 transition-all"
          disabled={isTransitioning}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* City indicator */}
      <div className="absolute bottom-8 left-8 z-20">
        <span className="text-[#d9fb06]/60 text-sm font-medium uppercase tracking-widest">
          {heroImages[currentSlide]?.city}
        </span>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[87.5rem] mx-auto px-6 md:px-10 py-32 md:py-40">
        <div className="max-w-3xl">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-6">
            <span className="text-[#888680] text-sm font-medium uppercase tracking-widest">
              {t.hero.eyebrow}
            </span>
          </div>

          {/* Main Title */}
          <h1 className="font-black text-[#d9fb06] text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.9] uppercase tracking-tight mb-6">
            {t.hero.title}
          </h1>

          {/* Subtitle */}
          <p className="text-[#d9fb06]/90 text-base sm:text-lg md:text-xl font-medium leading-relaxed max-w-2xl mb-10">
            {t.hero.subtitle}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={scrollToContact}
              className="btn-primary bg-[#d9fb06] text-[#1a1c1b] rounded-full px-6 sm:px-8 py-4 font-semibold text-sm uppercase tracking-wide hover:scale-[1.02] active:scale-[0.98] transition-transform inline-flex items-center justify-center gap-2"
            >
              {t.hero.ctaPrimary}
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={scrollToEvent}
              className="btn-secondary border border-[#d9fb06] text-[#d9fb06] rounded-full px-6 sm:px-8 py-4 font-semibold text-sm uppercase tracking-wide hover:bg-[#d9fb06] hover:text-[#1a1c1b] transition-all inline-flex items-center justify-center gap-2"
            >
              {t.hero.ctaSecondary}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#1a1c1b] to-transparent z-10" />
    </section>
  );
};

export default HeroSection;
